import { ApolloQueryResult } from 'apollo-client';

import {
    campaignsIdsQuery,
    dailyBudgetRecommendationsGridQuery,
    recommendationsGridSummaryQuery,
    recommendationsPageQuery,
    recommendationsStatuses,
    removePagesFromBlackListRecommendationsGridQuery,
    switchOnAutotargetingRecommendationsGridQuery
} from '../../graphql/queries/grid/recommendations';
import { mapSortParamsToString } from '../../mappers/common/common';
import {
    mapDailyBudgetRecommendationsItemFromApiFormat,
    mapRemovePagesFromBlackListRecommendationsItemFromApiFormat,
    mapSwitchOnAutotargetingRecommendationsItemFromApiFormat
} from '../../mappers/recommendationsGrid';
import { RUM, rumCounters } from '../../service/RUM/RUM';
import {
    CampaignsIdsQuery,
    DailyBudgetRecommendationsGridQuery,
    GdiRecommendationTypeInput,
    GdStatPresetInput,
    RecommendationsGridSummaryQuery,
    recommendationsGridSummaryRowFragment,
    RecommendationsPageQuery,
    RecommendationsStatusesQuery,
    RemovePagesFromBlackListRecommendationGridQuery,
    summaryRecommendationFragment,
    SwitchOnAutotargetingRecommendationsGridQuery
} from '../../typings/graphql-grid';
import { SortParams } from '../../typings/grid';
import {
    DailyBudgetRecommendationsItem,
    RecommendationKey,
    RecommendationType,
    RemovePagesFromBlackListRecommendationsItem,
    SwitchOnAutotargetingRecommendationsItem
} from '../../typings/recommendations';
import { RecommendationsGridStateClientInfo } from '../../typings/state';
import { BaseDataProvider } from '../BaseDataProvider/BaseDataProvider';

export interface FetchRecommendationsScreenDataResult {
    data: summaryRecommendationFragment[];
    reqId: string;
}

export type FetchRecommendationsItemsScreenSummaryDataResult = {
    data: recommendationsGridSummaryRowFragment[];
};

export type FetchRecommendationsItemsScreenDataResult = {
    clientInfo: RecommendationsGridStateClientInfo;
    data: {
        data: Array<
            | DailyBudgetRecommendationsItem
            | RemovePagesFromBlackListRecommendationsItem
            | SwitchOnAutotargetingRecommendationsItem
        >;
        allItemsIds: string[];
        total: number;
    };
    reqId: string;
};

export class RecommendationsDataProvider extends BaseDataProvider {
    public fetchRecommendationsScreenData = (
        login: string
    ): Promise<FetchRecommendationsScreenDataResult> => {
        return this.createQuery<RecommendationsPageQuery>(
            recommendationsPageQuery,
            {
                variables: {
                    login
                }
            },
            rumCounters.backendRecommendations
        ).then(
            (
                result: ApolloQueryResult<RecommendationsPageQuery>
            ): FetchRecommendationsScreenDataResult => {
                return {
                    data: result.data.client.recommendationsSummary.rowset,
                    reqId: result.data.reqId
                };
            }
        );
    };

    public fetchRecommendationsItemsScreenGridData = (
        login: string,
        type: RecommendationType,
        offset: number,
        count: number,
        campaignsIds?: Array<string>,
        sortParams?: SortParams
    ): Promise<FetchRecommendationsItemsScreenDataResult> => {
        const viewToMethodDict = {
            [RecommendationType.dailyBudget]: this
                .fetchDailyBudgetRecommendationsItemsScreenGridData,
            [RecommendationType.removePagesFromBlackList]: this
                .fetchRemovePagesFromBlackListRecommendationsItemsScreenGridData,
            [RecommendationType.switchOnAutotargeting]: this
                .fetchSwitchOnAutotargetingRecommendationsItemsScreenGridData
        };

        if (!viewToMethodDict[type]) {
            throw Error(`Нет запроса для рекомендации c типом ${type}`);
        }

        const fn = viewToMethodDict[type];

        return fn(login, offset, count, campaignsIds, sortParams);
    };

    public fetchRecommendationsItemsScreenSummaryData = (
        login: string,
        type: RecommendationType
    ): Promise<FetchRecommendationsItemsScreenSummaryDataResult> => {
        return this.createQuery<RecommendationsGridSummaryQuery>(
            recommendationsGridSummaryQuery,
            {
                variables: {
                    login,
                    types: [type]
                }
            }
        ).then(result => {
            return {
                data: result.data.client.recommendationsSummary.rowset,
                reqId: result.data.reqId
            };
        });
    };

    public fetchCampaignsIds(
        login: string,
        type: RecommendationType,
        sortParams?: SortParams
    ): Promise<Array<string>> {
        return this.createQuery<CampaignsIdsQuery>(campaignsIdsQuery, {
            variables: {
                login,
                campaignInput: {
                    limitOffset: {
                        offset: 0,
                        limit: 10000
                    },
                    statRequirements: {
                        preset: GdStatPresetInput.RECOMMENDATIONS_PERIOD
                    },
                    orderBy: [],
                    filter: {
                        recommendations: [type]
                    }
                }
            }
        }).then(result =>
            result.data.client.campaigns.rowset.map(item => item.id)
        );
    }

    public getRecommendationsStatus(
        login: string,
        keys: Array<RecommendationKey>
    ): Promise<RecommendationsStatusesQuery> {
        return this.createQuery<RecommendationsStatusesQuery>(
            recommendationsStatuses,
            {
                variables: {
                    login,
                    keys
                }
            }
        ).then(result => result.data);
    }

    private fetchDailyBudgetRecommendationsItemsScreenGridData = (
        login: string,
        offset: number,
        count: number,
        campaignsIds?: Array<number>,
        sortParams?: SortParams
    ): Promise<FetchRecommendationsItemsScreenDataResult> => {
        const query = this.createQuery<DailyBudgetRecommendationsGridQuery>(
            dailyBudgetRecommendationsGridQuery,
            {
                variables: {
                    login,
                    campaignInput: {
                        limitOffset: {
                            offset,
                            limit: count
                        },
                        statRequirements: {
                            preset: GdStatPresetInput.RECOMMENDATIONS_PERIOD
                        },
                        orderBy: mapSortParamsToString(sortParams),
                        filter: {
                            recommendations: [
                                GdiRecommendationTypeInput.dailyBudget
                            ]
                        }
                    }
                }
            },
            rumCounters.backendGridRecommendationsDailyBudget
        );

        return query.then(
            (result: ApolloQueryResult<DailyBudgetRecommendationsGridQuery>) =>
                RUM.reportSyncDelta(
                    rumCounters.clientGridRecommendationsDailyBudgetMapper,
                    result.data.reqId,
                    () => {
                        const data = result.data.client;
                        const reqId = result.data.reqId;
                        const campaignsInfo = data.campaigns;

                        return {
                            clientInfo: {
                                ...data.features,
                                ...data.info
                            },
                            data: {
                                data: campaignsInfo.rowset.map(
                                    mapDailyBudgetRecommendationsItemFromApiFormat
                                ),
                                total: campaignsInfo.totalCount,
                                allItemsIds: campaignsInfo.campaignIds
                            },
                            reqId
                        };
                    }
                )
        );
    };

    private fetchRemovePagesFromBlackListRecommendationsItemsScreenGridData = (
        login: string,
        offset: number,
        count: number,
        campaignsIds?: Array<number>,
        sortParams?: SortParams
    ): Promise<FetchRecommendationsItemsScreenDataResult> => {
        const query = this.createQuery<
            RemovePagesFromBlackListRecommendationGridQuery
        >(
            removePagesFromBlackListRecommendationsGridQuery,
            {
                variables: {
                    orderBy: mapSortParamsToString(sortParams),
                    login,
                    offset,
                    count
                }
            },
            rumCounters.backendGridRecommendationsRemovePagesFromBlackList
        );

        return query.then(
            (
                result: ApolloQueryResult<
                    RemovePagesFromBlackListRecommendationGridQuery
                >
            ) =>
                RUM.reportSyncDelta(
                    rumCounters.clientGridRecommendationsRemovePagesFromBlackListMapper,
                    result.data.reqId,
                    () => {
                        const data = result.data.client;
                        const reqId = result.data.reqId;
                        const recommendationsInfo = data.forbiddenPages;

                        return {
                            clientInfo: {
                                ...data.features,
                                ...data.info
                            },
                            data: {
                                data: recommendationsInfo.rowset.map(
                                    mapRemovePagesFromBlackListRecommendationsItemFromApiFormat
                                ),
                                total: recommendationsInfo.totalCount,
                                allItemsIds: []
                            },
                            reqId
                        };
                    }
                )
        );
    };

    private fetchSwitchOnAutotargetingRecommendationsItemsScreenGridData = (
        login: string,
        offset: number,
        count: number,
        campaignsIds?: Array<string>,
        sortParams?: SortParams
    ): Promise<FetchRecommendationsItemsScreenDataResult> => {
        const query = this.createQuery<
            SwitchOnAutotargetingRecommendationsGridQuery
        >(
            switchOnAutotargetingRecommendationsGridQuery,
            {
                variables: {
                    login,
                    adGroupInput: {
                        limitOffset: {
                            offset,
                            limit: count
                        },
                        statRequirements: {
                            preset: GdStatPresetInput.RECOMMENDATIONS_PERIOD
                        },
                        orderBy: mapSortParamsToString(sortParams),
                        filter: {
                            recommendations: [
                                GdiRecommendationTypeInput.switchOnAutotargeting
                            ],
                            campaignIdIn: campaignsIds
                        }
                    }
                }
            },
            rumCounters.backendGridRecommendationsSwitchOnAutotargeting
        );

        return query.then(
            (
                result: ApolloQueryResult<
                    SwitchOnAutotargetingRecommendationsGridQuery
                >
            ) =>
                RUM.reportSyncDelta(
                    rumCounters.clientGridRecommendationsSwitchOnAutotargetingMapper,
                    result.data.reqId,
                    () => {
                        const data = result.data.client;
                        const reqId = result.data.reqId;
                        const recommendationsInfo = data.adGroups;

                        return {
                            clientInfo: {
                                ...data.features,
                                ...data.info
                            },
                            data: {
                                data: recommendationsInfo.rowset.map(
                                    mapSwitchOnAutotargetingRecommendationsItemFromApiFormat
                                ),
                                total: recommendationsInfo.totalCount,
                                allItemsIds: []
                            },
                            reqId
                        };
                    }
                )
        );
    };
}
